import { HtmlContent } from "@/components/Common/HtmlContent"
import {
  Badge,
  Box,
  Card,
  HStack,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react"
import type { AnswerChoice } from "../../../client"

interface AnswerDistributionChartProps {
  data: AnswerChoice[]
  questionText?: string | null
  questionNumber?: number
}

export default function AnswerDistributionChart({
  data,
  questionText,
  questionNumber,
}: AnswerDistributionChartProps) {
  if (data.length === 0) {
    return (
      <Card.Root h="full">
        <Card.Body>
          <Heading size="md" mb={4}>
            {questionNumber ? `#${questionNumber} - ` : ""}Answer Distribution
          </Heading>
          {questionText ? (
            <HtmlContent
              content={questionText}
              fontSize="sm"
              color="gray.600"
              mb={4}
              css={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            />
          ) : null}
          <Text color="gray.500">No data available</Text>
        </Card.Body>
      </Card.Root>
    )
  }

  return (
    <Card.Root h="full">
      <Card.Body>
        <Heading size="md" mb={4}>
          {questionNumber ? `#${questionNumber} - ` : ""}Answer Distribution
        </Heading>
        {questionText ? (
          <HtmlContent
            content={questionText}
            fontSize="sm"
            color="gray.600"
            mb={4}
            css={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          />
        ) : null}
        <Text fontSize="sm" color="gray.600" mb={4}>
          Which options students selected
        </Text>

        <VStack align="stretch" gap={3}>
          {data.map((choice) => (
            <Box key={choice.option_index}>
              <HStack justify="space-between" mb={1}>
                <HStack>
                  <Text fontSize="sm" fontWeight="medium">
                    Option {String.fromCharCode(65 + choice.option_index)}
                  </Text>
                  {choice.is_correct && (
                    <Badge colorScheme="green" size="sm">
                      Correct
                    </Badge>
                  )}
                </HStack>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={choice.is_correct ? "green.600" : "gray.600"}
                >
                  {choice.selection_percentage.toFixed(1)}%
                </Text>
              </HStack>

              <Box mb={2}>
                <Text fontSize="xs" color="gray.600" truncate>
                  {choice.option_text}
                </Text>
              </Box>

              <Box bg="gray.100" h="8px" borderRadius="full" overflow="hidden">
                <Box
                  bg={choice.is_correct ? "green.400" : "red.300"}
                  h="full"
                  w={`${choice.selection_percentage}%`}
                  transition="width 0.3s"
                />
              </Box>
            </Box>
          ))}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
